/**
 * Customers, khata and rewards contracts (M5 task 4; 03-modules.md §15, 04-data-model.md
 * "customers", 05-api.md "Customers, khata, rewards", 06-flows.md F2 step 9).
 *
 * Money is integer paise. A khata balance is the sum of the account's ledger rows: positive
 * is what the customer owes the outlet (dues). A reward balance is the sum of the customer's
 * reward ledger rows at an outlet, in paise of redemption value (a rupee of reward is worth a
 * rupee at billing, whatever the program calls it).
 */
import { z } from 'zod';
import { businessDateSchema, isoTimestampSchema, pageQuerySchema } from './common.js';
import { defineEvent } from './events.js';
import { uuidSchema } from './ids.js';
import { nonNegativePaiseSchema, paiseSchema } from './money.js';
import { orderSchema } from './orders.js';
import { gstinSchema } from './org.js';
import { paymentSchema } from './payments.js';

const positivePaise = nonNegativePaiseSchema.refine((v) => v > 0n, {
  message: 'Must be more than zero',
});

// ---------------------------------------------------------------------------
// Customer master

/** A phone number as a person types it; `normalizePhone` (packages/domain) makes the key. */
export const customerPhoneSchema = z
  .string()
  .trim()
  .min(6)
  .max(20)
  .regex(/^[+\d][\d\s\-()]{5,19}$/, { message: 'Expected a phone number' });

export const customerNameSchema = z.string().trim().min(1).max(120);

/** What the customer agreed to (DPDP: kept as given, never assumed). */
export const customerConsentSchema = z.object({
  marketing: z.boolean().optional(),
  whatsapp: z.boolean().optional(),
  sms: z.boolean().optional(),
  email: z.boolean().optional(),
});
export type CustomerConsent = z.output<typeof customerConsentSchema>;

export const customerTagSchema = z.string().trim().min(1).max(30);
export const customerTagsSchema = z
  .array(customerTagSchema)
  .max(20)
  .refine((v) => new Set(v.map((t) => t.toLowerCase())).size === v.length, {
    message: 'Each tag once',
  });

export const MAX_ADDRESSES_PER_CUSTOMER = 20;

export const customerAddressSchema = z.object({
  id: uuidSchema,
  customerId: uuidSchema,
  label: z.string(),
  line1: z.string(),
  line2: z.string().nullable(),
  landmark: z.string().nullable(),
  city: z.string().nullable(),
  pincode: z.string().nullable(),
  /** Where the address is on the map (the customer app places it); null when not placed. */
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  isDefault: z.boolean(),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
});
export type CustomerAddressView = z.output<typeof customerAddressSchema>;

export const customerNoteSchema = z.object({
  id: uuidSchema,
  customerId: uuidSchema,
  body: z.string(),
  authorId: uuidSchema.nullable(),
  createdAt: isoTimestampSchema,
});
export type CustomerNoteView = z.output<typeof customerNoteSchema>;

/** A row of the customer list. */
export const customerSummarySchema = z.object({
  id: uuidSchema,
  name: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  tags: z.array(z.string()),
  originOutletId: uuidSchema.nullable(),
  erasedAt: isoTimestampSchema.nullable(),
  createdAt: isoTimestampSchema,
});
export type CustomerSummary = z.output<typeof customerSummarySchema>;

export const customerSchema = customerSummarySchema.extend({
  birthday: businessDateSchema.nullable(),
  gstin: z.string().nullable(),
  consent: customerConsentSchema,
  updatedAt: isoTimestampSchema,
  version: z.int(),
});
export type CustomerView = z.output<typeof customerSchema>;

export const customerListQuerySchema = pageQuerySchema.extend({
  /** Name or phone (digits, any part). */
  q: z.string().trim().min(1).max(60).optional(),
  tag: customerTagSchema.optional(),
});
export const customerListSchema = z.object({
  data: z.array(customerSummarySchema),
  nextCursor: z.string().nullable(),
});

export const customerParamsSchema = z.object({ customerId: uuidSchema });
export const customerAddressParamsSchema = z.object({
  customerId: uuidSchema,
  addressId: uuidSchema,
});

const customerFields = {
  name: customerNameSchema,
  email: z.string().trim().toLowerCase().email().max(200).nullable(),
  birthday: businessDateSchema.nullable(),
  gstin: gstinSchema.nullable(),
  tags: customerTagsSchema,
  consent: customerConsentSchema,
  originOutletId: uuidSchema.nullable(),
};

/**
 * `POST /v1/customers`: a customer by phone; the phone is the key within the company, so a
 * phone that is already a customer's updates that customer with the fields given (the till
 * adding "the same person again" is not an error). `id` lets an offline till mint it.
 */
export const createCustomerSchema = z.object({
  id: uuidSchema.optional(),
  phone: customerPhoneSchema,
  name: customerFields.name,
  email: customerFields.email.optional(),
  birthday: customerFields.birthday.optional(),
  gstin: customerFields.gstin.optional(),
  tags: customerFields.tags.optional(),
  consent: customerFields.consent.optional(),
  originOutletId: customerFields.originOutletId.optional(),
});
export type CreateCustomerBody = z.output<typeof createCustomerSchema>;

/** `PATCH /v1/customers/{id}`: only what is given changes. */
export const updateCustomerSchema = z.object({
  phone: customerPhoneSchema.optional(),
  name: customerFields.name.optional(),
  email: customerFields.email.optional(),
  birthday: customerFields.birthday.optional(),
  gstin: customerFields.gstin.optional(),
  tags: customerFields.tags.optional(),
  consent: customerFields.consent.optional(),
});
export type UpdateCustomerBody = z.output<typeof updateCustomerSchema>;

/** `POST /v1/customers/{id}/addresses` and its PATCH. */
export const saveAddressSchema = z.object({
  id: uuidSchema.optional(),
  label: z.string().trim().min(1).max(40).default('Home'),
  line1: z.string().trim().min(1).max(200),
  line2: z.string().trim().max(200).nullable().optional(),
  landmark: z.string().trim().max(120).nullable().optional(),
  city: z.string().trim().max(80).nullable().optional(),
  pincode: z
    .string()
    .trim()
    .regex(/^\d{6}$/, { message: 'Expected a 6-digit pincode' })
    .nullable()
    .optional(),
  lat: z.number().min(-90).max(90).nullable().optional(),
  lng: z.number().min(-180).max(180).nullable().optional(),
  isDefault: z.boolean().optional(),
});
export type SaveAddressBody = z.output<typeof saveAddressSchema>;
export const updateAddressSchema = saveAddressSchema.partial().omit({ id: true });

export const addNoteSchema = z.object({
  id: uuidSchema.optional(),
  body: z.string().trim().min(1).max(1000),
});

// ---------------------------------------------------------------------------
// Khata

export const KHATA_STATUSES = ['active', 'suspended', 'closed'] as const;
export type KhataStatus = (typeof KHATA_STATUSES)[number];

export const KHATA_LEDGER_REASONS = ['charge', 'payment', 'reversal', 'adjust'] as const;
export type KhataLedgerReason = (typeof KHATA_LEDGER_REASONS)[number];

/** How dues were paid when a khata payment is recorded (a khata payment is never khata). */
export const KHATA_RECEIPT_METHODS = ['cash', 'card', 'upi', 'other'] as const;
export type KhataReceiptMethod = (typeof KHATA_RECEIPT_METHODS)[number];

export const khataAccountSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  customerId: uuidSchema,
  customerName: z.string(),
  customerPhone: z.string().nullable(),
  limitPaise: paiseSchema,
  status: z.enum(KHATA_STATUSES),
  /** Sum of the ledger: what the customer owes (negative: paid in advance). */
  balancePaise: paiseSchema,
  /** `limit - balance`, never below zero. */
  availablePaise: paiseSchema,
  lastMovementAt: isoTimestampSchema.nullable(),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
  version: z.int(),
});
export type KhataAccountView = z.output<typeof khataAccountSchema>;

export const khataListQuerySchema = pageQuerySchema.extend({
  /** `due`: balance above zero. */
  status: z.enum(['due', ...KHATA_STATUSES]).optional(),
  customerId: uuidSchema.optional(),
  q: z.string().trim().min(1).max(60).optional(),
});
export const khataListSchema = z.object({
  data: z.array(khataAccountSchema),
  nextCursor: z.string().nullable(),
  /** The sum of dues over the whole filter (not just this page). */
  totalDuePaise: paiseSchema,
});

export const khataParamsSchema = z.object({ outletId: uuidSchema, accountId: uuidSchema });

/** `POST {o}/khata`: open an account for a customer at this outlet. */
export const openKhataSchema = z.object({
  id: uuidSchema.optional(),
  customerId: uuidSchema,
  limitPaise: nonNegativePaiseSchema,
});

/** `PATCH {o}/khata/{id}`: the credit limit and/or the status. */
export const updateKhataSchema = z
  .object({
    limitPaise: nonNegativePaiseSchema.optional(),
    status: z.enum(KHATA_STATUSES).optional(),
  })
  .refine((v) => v.limitPaise !== undefined || v.status !== undefined, {
    message: 'Give a limit or a status',
  });

export const khataEntrySchema = z.object({
  id: uuidSchema,
  seq: z.int(),
  accountId: uuidSchema,
  deltaPaise: paiseSchema,
  reason: z.enum(KHATA_LEDGER_REASONS),
  refType: z.string(),
  refId: uuidSchema.nullable(),
  orderId: uuidSchema.nullable(),
  /** The account's balance after this row. */
  balanceAfterPaise: paiseSchema,
  method: z.enum(KHATA_RECEIPT_METHODS).nullable(),
  note: z.string().nullable(),
  actorId: uuidSchema.nullable(),
  businessDate: businessDateSchema,
  at: isoTimestampSchema,
});
export type KhataEntryView = z.output<typeof khataEntrySchema>;

/** `GET {o}/khata/{id}/ledger`: the statement of a period. */
export const khataStatementQuerySchema = pageQuerySchema.extend({
  from: businessDateSchema.optional(),
  to: businessDateSchema.optional(),
});
export const khataStatementSchema = z.object({
  account: khataAccountSchema,
  from: businessDateSchema.nullable(),
  to: businessDateSchema.nullable(),
  /** The balance before the first row of the period. */
  openingPaise: paiseSchema,
  /** The balance after the last row of the period. */
  closingPaise: paiseSchema,
  chargesPaise: paiseSchema,
  paymentsPaise: paiseSchema,
  entries: z.array(khataEntrySchema),
  nextCursor: z.string().nullable(),
});
export type KhataStatementView = z.output<typeof khataStatementSchema>;

/**
 * `POST {o}/orders/{id}/khata-charge`: put (part of) what the order owes on the order's
 * customer's khata, as a tender. Within the limit with `khata.charge`; above it
 * `khata.charge.above_limit` (a manager's override).
 */
export const chargeKhataSchema = z.object({
  /** The tender's payment id, so a retry answers the same charge. */
  paymentId: uuidSchema.optional(),
  /** Default: everything still due. */
  amountPaise: positivePaise.optional(),
});

export const khataChargeResultSchema = z.object({
  payment: paymentSchema,
  order: orderSchema,
  account: khataAccountSchema,
  entry: khataEntrySchema,
});
export type KhataChargeResult = z.output<typeof khataChargeResultSchema>;

/** `POST {o}/khata/{id}/settle`: the customer pays against their dues. */
export const settleKhataSchema = z.object({
  /** The receipt's id, so a retry records one payment. */
  receiptId: uuidSchema.optional(),
  amountPaise: positivePaise,
  method: z.enum(KHATA_RECEIPT_METHODS),
  note: z.string().trim().max(300).optional(),
});
export const khataSettleResultSchema = z.object({
  account: khataAccountSchema,
  entry: khataEntrySchema,
});
export type KhataSettleResult = z.output<typeof khataSettleResultSchema>;

// ---------------------------------------------------------------------------
// Reward programs

export const REWARD_PROGRAM_KINDS = ['points', 'cashback'] as const;
export type RewardProgramKind = (typeof REWARD_PROGRAM_KINDS)[number];

export const rewardSlabSchema = z.object({
  id: uuidSchema,
  programId: uuidSchema,
  minBillPaise: paiseSchema,
  /** Null: no upper bill. */
  maxBillPaise: paiseSchema.nullable(),
  /** Earn this share of the bill (basis points), or ... */
  earnBp: z.int().nullable(),
  /** ... this fixed amount. Exactly one is set. */
  earnPaise: paiseSchema.nullable(),
  /** Days the earned reward lasts; null: never expires. */
  expiryDays: z.int().nullable(),
  archivedAt: isoTimestampSchema.nullable(),
  version: z.int(),
});
export type RewardSlabView = z.output<typeof rewardSlabSchema>;

export const rewardProgramSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  name: z.string(),
  kind: z.enum(REWARD_PROGRAM_KINDS),
  active: z.boolean(),
  /** The least balance a customer must hold to redeem. */
  minRedeemPaise: paiseSchema,
  /** The most of one bill that rewards may pay, in basis points of the order total. */
  maxRedeemBp: z.int(),
  slabs: z.array(rewardSlabSchema),
  archivedAt: isoTimestampSchema.nullable(),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
  version: z.int(),
});
export type RewardProgramView = z.output<typeof rewardProgramSchema>;
export const rewardProgramListSchema = z.object({ data: z.array(rewardProgramSchema) });

export const rewardProgramParamsSchema = z.object({ outletId: uuidSchema, programId: uuidSchema });
export const rewardSlabParamsSchema = z.object({ outletId: uuidSchema, slabId: uuidSchema });

export const createRewardProgramSchema = z.object({
  id: uuidSchema.optional(),
  name: z.string().trim().min(1).max(80),
  kind: z.enum(REWARD_PROGRAM_KINDS),
  /** Default off: set the slabs first. */
  active: z.boolean().optional(),
  minRedeemPaise: nonNegativePaiseSchema.optional(),
  maxRedeemBp: z.int().min(0).max(10_000).optional(),
});
export const updateRewardProgramSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    kind: z.enum(REWARD_PROGRAM_KINDS),
    active: z.boolean(),
    minRedeemPaise: nonNegativePaiseSchema,
    maxRedeemBp: z.int().min(0).max(10_000),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Give at least one field' });

const slabFields = {
  minBillPaise: nonNegativePaiseSchema,
  maxBillPaise: nonNegativePaiseSchema.nullable(),
  earnBp: z.int().min(1).max(10_000).nullable(),
  earnPaise: positivePaise.nullable(),
  expiryDays: z.int().min(1).max(3650).nullable(),
};
const oneEarn = (v: {
  earnBp?: number | null | undefined;
  earnPaise?: bigint | null | undefined;
}) => (v.earnBp != null) !== (v.earnPaise != null);

/** `POST {o}/reward-slabs`: a bill range and what it earns. */
export const createRewardSlabSchema = z
  .object({
    id: uuidSchema.optional(),
    programId: uuidSchema,
    minBillPaise: slabFields.minBillPaise,
    maxBillPaise: slabFields.maxBillPaise.optional(),
    earnBp: slabFields.earnBp.optional(),
    earnPaise: slabFields.earnPaise.optional(),
    expiryDays: slabFields.expiryDays.optional(),
  })
  .refine(oneEarn, { message: 'Give earnBp or earnPaise (one of them)', path: ['earnBp'] })
  .refine((v) => v.maxBillPaise == null || v.maxBillPaise >= v.minBillPaise, {
    message: 'The top of the range cannot be below its start',
    path: ['maxBillPaise'],
  });
export const updateRewardSlabSchema = z
  .object({
    minBillPaise: slabFields.minBillPaise,
    maxBillPaise: slabFields.maxBillPaise,
    earnBp: slabFields.earnBp,
    earnPaise: slabFields.earnPaise,
    expiryDays: slabFields.expiryDays,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Give at least one field' });

// ---------------------------------------------------------------------------
// Rewards of a customer

export const REWARD_LEDGER_REASONS = ['earn', 'redeem', 'expire', 'adjust', 'reversal'] as const;
export type RewardLedgerReason = (typeof REWARD_LEDGER_REASONS)[number];

export const rewardBalanceSchema = z.object({
  customerId: uuidSchema,
  outletId: uuidSchema,
  /** What can be redeemed now (lots not expired). */
  balancePaise: paiseSchema,
  /** Of that, what expires within the next 30 days. */
  expiringSoonPaise: paiseSchema,
  nextExpiryAt: isoTimestampSchema.nullable(),
});
export type RewardBalanceView = z.output<typeof rewardBalanceSchema>;

export const rewardEntrySchema = z.object({
  id: uuidSchema,
  seq: z.int(),
  customerId: uuidSchema,
  outletId: uuidSchema,
  deltaPaise: paiseSchema,
  reason: z.enum(REWARD_LEDGER_REASONS),
  lotId: uuidSchema,
  expiresAt: isoTimestampSchema.nullable(),
  orderId: uuidSchema.nullable(),
  refType: z.string().nullable(),
  refId: uuidSchema.nullable(),
  note: z.string().nullable(),
  at: isoTimestampSchema,
});
export type RewardEntryView = z.output<typeof rewardEntrySchema>;

export const customerRewardsSchema = z.object({
  customerId: uuidSchema,
  balances: z.array(rewardBalanceSchema),
  entries: z.array(rewardEntrySchema),
  nextCursor: z.string().nullable(),
});
export type CustomerRewardsView = z.output<typeof customerRewardsSchema>;
export const customerRewardsQuerySchema = pageQuerySchema.extend({
  outletId: uuidSchema.optional(),
});

/** `POST /v1/customers/{id}/rewards/adjust`: a correction by a manager (permissioned, audited). */
export const adjustRewardsSchema = z.object({
  adjustmentId: uuidSchema.optional(),
  outletId: uuidSchema,
  /** Positive gives reward value (it expires like a slab's with `expiryDays`); negative takes it. */
  deltaPaise: paiseSchema.refine((v) => v !== 0n, { message: 'Must not be zero' }),
  expiryDays: z.int().min(1).max(3650).nullable().optional(),
  note: z.string().trim().min(1).max(300),
});
export const adjustRewardsResultSchema = z.object({
  balance: rewardBalanceSchema,
  entries: z.array(rewardEntrySchema),
});

/** `GET {o}/orders/{id}/rewards`: what the order's customer can redeem on this bill. */
export const orderRewardsSchema = z.object({
  orderId: uuidSchema,
  customerId: uuidSchema.nullable(),
  programId: uuidSchema.nullable(),
  balancePaise: paiseSchema,
  minRedeemPaise: paiseSchema,
  /** Most that can be redeemed now: balance, the bill's share, what is still due. */
  maxRedeemablePaise: paiseSchema,
  /** Why nothing can be redeemed (for the till to show), or null. */
  unavailableReason: z
    .enum(['no_customer', 'no_program', 'below_minimum', 'nothing_due', 'no_balance'])
    .nullable(),
});
export type OrderRewardsView = z.output<typeof orderRewardsSchema>;

/**
 * `POST {o}/orders/{id}/rewards/redeem`: pay (part of) the bill with the customer's rewards.
 * The server decides the figure's limit; a tender, so the bill and its tax do not change.
 */
export const redeemRewardsSchema = z.object({
  paymentId: uuidSchema.optional(),
  /** Default: the most that can be redeemed. */
  amountPaise: positivePaise.optional(),
});
export const redeemRewardsResultSchema = z.object({
  payment: paymentSchema,
  order: orderSchema,
  balance: rewardBalanceSchema,
});
export type RedeemRewardsResult = z.output<typeof redeemRewardsResultSchema>;

// ---------------------------------------------------------------------------
// Customer on an order

/** `POST {o}/orders/{id}/customer`: attach a customer (or null: remove). */
export const attachCustomerSchema = z.object({ customerId: uuidSchema.nullable() });
export const orderCustomerResultSchema = z.object({
  order: orderSchema,
  customer: customerSummarySchema.nullable(),
});

// ---------------------------------------------------------------------------
// Detail, import, DPDP

export const customerDetailSchema = z.object({
  customer: customerSchema,
  addresses: z.array(customerAddressSchema),
  notes: z.array(customerNoteSchema),
  /** The customer's khata accounts at the outlets the caller can see. */
  khata: z.array(khataAccountSchema),
  /** Their reward balances at those outlets. */
  rewards: z.array(rewardBalanceSchema),
});
export type CustomerDetailView = z.output<typeof customerDetailSchema>;

export const CUSTOMER_IMPORT_MAX_BYTES = 1024 * 1024;
export const CUSTOMER_IMPORT_MAX_ROWS = 5000;
export const CUSTOMER_IMPORT_COLUMNS = [
  'Phone',
  'Name',
  'Email',
  'Birthday',
  'GSTIN',
  'Tags',
  'Marketing consent',
] as const;

/**
 * `POST /v1/customers/import`: a CSV or Excel file (base64). `dryRun` previews what would
 * change and writes nothing; without it the rows are applied in one transaction when there
 * are no errors (all or nothing). A file name and its hash make the import traceable.
 */
export const customerImportRequestSchema = z.object({
  fileName: z.string().trim().min(1).max(200).optional(),
  format: z.enum(['csv', 'xlsx']),
  contentBase64: z
    .string()
    .min(4)
    .max(Math.ceil(CUSTOMER_IMPORT_MAX_BYTES / 3) * 4 + 4)
    .regex(/^[A-Za-z0-9+/]+={0,2}$/, { message: 'Expected base64' }),
  dryRun: z.boolean().default(true),
  /** Customers whose phone already exists: update them (default) or leave them. */
  updateExisting: z.boolean().default(true),
  /** The outlet new customers are recorded as coming from. */
  originOutletId: uuidSchema.nullable().optional(),
});

export const customerImportResultSchema = z.object({
  dryRun: z.boolean(),
  applied: z.boolean(),
  importId: uuidSchema.nullable(),
  summary: z.object({
    rows: z.int(),
    created: z.int(),
    updated: z.int(),
    unchanged: z.int(),
    errors: z.int(),
  }),
  changes: z.array(
    z.object({
      row: z.int(),
      action: z.enum(['create', 'update', 'unchanged']),
      phone: z.string(),
      name: z.string(),
      fields: z.array(z.string()),
    }),
  ),
  errors: z.array(z.object({ row: z.int(), field: z.string().nullable(), message: z.string() })),
  canApply: z.boolean(),
});
export type CustomerImportResult = z.output<typeof customerImportResultSchema>;

/** `POST /v1/customers/{id}/erase` (DPDP): the dues must be settled first; irreversible. */
export const eraseCustomerSchema = z.object({
  /** The customer's request, kept in the audit trail (reference, date, channel). */
  reason: z.string().trim().min(1).max(300),
});
export const eraseResultSchema = z.object({
  customerId: uuidSchema,
  erasedAt: isoTimestampSchema,
  anonymisedOrders: z.int(),
  deletedAddresses: z.int(),
  deletedNotes: z.int(),
});

/** `POST /v1/customers/{id}/export` (DPDP): everything held about the customer. */
export const customerDataExportSchema = z.object({
  exportedAt: isoTimestampSchema,
  customer: customerSchema,
  addresses: z.array(customerAddressSchema),
  notes: z.array(customerNoteSchema),
  khata: z.array(z.object({ account: khataAccountSchema, entries: z.array(khataEntrySchema) })),
  rewards: z.array(z.object({ balance: rewardBalanceSchema, entries: z.array(rewardEntrySchema) })),
  orders: z.array(
    z.object({
      id: uuidSchema,
      outletId: uuidSchema,
      orderNo: z.int(),
      businessDate: businessDateSchema,
      status: z.string(),
      totalPaise: paiseSchema,
      invoiceId: uuidSchema.nullable(),
      createdAt: isoTimestampSchema,
    }),
  ),
});
export type CustomerDataExport = z.output<typeof customerDataExportSchema>;

// ---------------------------------------------------------------------------
// Events (03-modules.md §15 list, plus one for every other state change: rule 4).
// Payloads carry ids and amounts only: no names, phones or addresses.

const customerRef = { customerId: uuidSchema };
const khataRef = {
  accountId: uuidSchema,
  customerId: uuidSchema,
  outletId: uuidSchema,
};
const rewardRef = { customerId: uuidSchema, outletId: uuidSchema };

export const customerCreatedV1 = defineEvent(
  'customer.created',
  1,
  z.object({ ...customerRef, originOutletId: uuidSchema.nullable() }),
);
export const customerUpdatedV1 = defineEvent(
  'customer.updated',
  1,
  z.object({ ...customerRef, fields: z.array(z.string()) }),
);
export const customersImportedV1 = defineEvent(
  'customer.imported',
  1,
  z.object({
    importId: uuidSchema,
    fileSha256: z.string(),
    created: z.int(),
    updated: z.int(),
    unchanged: z.int(),
  }),
);
export const customerAddressSavedV1 = defineEvent(
  'customer.address_saved',
  1,
  z.object({ ...customerRef, addressId: uuidSchema }),
);
export const customerAddressRemovedV1 = defineEvent(
  'customer.address_removed',
  1,
  z.object({ ...customerRef, addressId: uuidSchema }),
);
export const customerNoteAddedV1 = defineEvent(
  'customer.note_added',
  1,
  z.object({ ...customerRef, noteId: uuidSchema }),
);
export const customerAttachedV1 = defineEvent(
  'customer.attached',
  1,
  z.object({
    orderId: uuidSchema,
    outletId: uuidSchema,
    /** Null: the customer was taken off the order. */
    customerId: uuidSchema.nullable(),
    previousCustomerId: uuidSchema.nullable(),
  }),
);
export const customerExportedV1 = defineEvent('customer.exported', 1, z.object({ ...customerRef }));
export const customerErasedV1 = defineEvent(
  'customer.erased',
  1,
  z.object({
    ...customerRef,
    anonymisedOrders: z.int(),
    deletedAddresses: z.int(),
    deletedNotes: z.int(),
  }),
);

export const khataOpenedV1 = defineEvent(
  'khata.opened',
  1,
  z.object({ ...khataRef, limitPaise: paiseSchema }),
);
export const khataUpdatedV1 = defineEvent(
  'khata.updated',
  1,
  z.object({
    ...khataRef,
    limitPaise: paiseSchema,
    status: z.enum(KHATA_STATUSES),
    previousLimitPaise: paiseSchema,
    previousStatus: z.enum(KHATA_STATUSES),
  }),
);
export const khataChargedV1 = defineEvent(
  'khata.charged',
  1,
  z.object({
    ...khataRef,
    orderId: uuidSchema,
    paymentId: uuidSchema,
    amountPaise: paiseSchema,
    balancePaise: paiseSchema,
    /** Charged beyond the credit limit with a manager's override. */
    aboveLimit: z.boolean(),
  }),
);
export const khataChargeReversedV1 = defineEvent(
  'khata.charge_reversed',
  1,
  z.object({
    ...khataRef,
    orderId: uuidSchema,
    paymentId: uuidSchema,
    amountPaise: paiseSchema,
    balancePaise: paiseSchema,
  }),
);
export const khataSettledV1 = defineEvent(
  'khata.settled',
  1,
  z.object({
    ...khataRef,
    receiptId: uuidSchema,
    amountPaise: paiseSchema,
    method: z.enum(KHATA_RECEIPT_METHODS),
    balancePaise: paiseSchema,
  }),
);

export const rewardProgramSavedV1 = defineEvent(
  'reward.program_saved',
  1,
  z.object({
    programId: uuidSchema,
    outletId: uuidSchema,
    active: z.boolean(),
    created: z.boolean(),
  }),
);
export const rewardSlabSavedV1 = defineEvent(
  'reward.slab_saved',
  1,
  z.object({
    slabId: uuidSchema,
    programId: uuidSchema,
    outletId: uuidSchema,
    action: z.enum(['created', 'updated', 'archived']),
  }),
);
export const rewardEarnedV1 = defineEvent(
  'reward.earned',
  1,
  z.object({
    ...rewardRef,
    orderId: uuidSchema,
    ledgerId: uuidSchema,
    amountPaise: paiseSchema,
    expiresAt: isoTimestampSchema.nullable(),
  }),
);
export const rewardRedeemedV1 = defineEvent(
  'reward.redeemed',
  1,
  z.object({
    ...rewardRef,
    orderId: uuidSchema,
    paymentId: uuidSchema,
    amountPaise: paiseSchema,
    balancePaise: paiseSchema,
  }),
);
export const rewardRedemptionReversedV1 = defineEvent(
  'reward.redemption_reversed',
  1,
  z.object({
    ...rewardRef,
    orderId: uuidSchema,
    paymentId: uuidSchema,
    amountPaise: paiseSchema,
    balancePaise: paiseSchema,
  }),
);
export const rewardExpiredV1 = defineEvent(
  'reward.expired',
  1,
  z.object({ ...rewardRef, amountPaise: paiseSchema, lots: z.int() }),
);
export const rewardAdjustedV1 = defineEvent(
  'reward.adjusted',
  1,
  z.object({ ...rewardRef, deltaPaise: paiseSchema, balancePaise: paiseSchema }),
);

export const CUSTOMER_EVENTS = [
  customerCreatedV1,
  customerUpdatedV1,
  customersImportedV1,
  customerAddressSavedV1,
  customerAddressRemovedV1,
  customerNoteAddedV1,
  customerAttachedV1,
  customerExportedV1,
  customerErasedV1,
  khataOpenedV1,
  khataUpdatedV1,
  khataChargedV1,
  khataChargeReversedV1,
  khataSettledV1,
  rewardProgramSavedV1,
  rewardSlabSavedV1,
  rewardEarnedV1,
  rewardRedeemedV1,
  rewardRedemptionReversedV1,
  rewardExpiredV1,
  rewardAdjustedV1,
] as const;
